import { TitleCasePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { DomainListItem } from '@app/core/interfaces/c5/domain-list-item';
import { EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { Enterprise } from '@app/core/interfaces/enterprise';
import { EnterpriseOption } from '@app/core/interfaces/enterprise-option';
import { SearchEnterprisePhoneNumbersParameters } from '@app/core/interfaces/search-enterprise-phone-numbers-parameters';
import { isServiceProvider, UserLevel } from '@app/core/models/user-level';
import { EnhancedEndUser } from '@app/shared/models/enhanced-end-user';
import { EnhancedEndUserLite } from '@app/shared/models/enhanced-end-user-lite';
import { ApiUrlParams, Utils } from '@app/shared/services/utils.service';
import { Identifier } from '@app/utils/common';
import { toHttpParams } from '@app/utils/http';
import { wrapError } from '@app/utils/rxjs';
import { enhanceUsersWithExtension } from '@app/utils/user';
import { EndUserLite } from '@business/end-user/models/end-user-lite';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { CivicAddress } from '../civic-address';
import { UpdateEnterpriseSBC } from '../interfaces/sbc';

@Injectable({
  providedIn: 'root',
})
export class EnterpriseRepository {
  constructor(
    private authenticationService: AuthenticationService,
    private http: HttpClient,
    private titleCasePipe: TitleCasePipe,
    private utils: Utils
  ) {}

  getEndUsers(): Observable<EnhancedEndUserLite[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId/endusers`,
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.Enterprise + `/endusers`,
    };

    return this.utils.apiUrl(urls).pipe(
      mergeMap((url) => this.http.get<EndUserLite[]>(url)),
      map((users) =>
        users.map((user) => ({
          ...user,
          extension: user.loginName.split('@')[0],
        }))
      ),
      wrapError('enterprise-repository:get-end-users')
    );
  }

  getEnterprise(enterpriseId?: Identifier): Observable<Enterprise> {
    return this.utils
      .apiUrl('/enterprise', { enterpriseId }, !isServiceProvider(this.authenticationService.authUser))
      .pipe(
        mergeMap((url) => this.http.get<Enterprise>(url)),
        wrapError('enterprise-repository:get-enterprise')
      );
  }

  /**
   * Will fetch the enterprise by name over the special '/redir' path from backend.
   * If "hydra" system distribution is employed then it will set the 'hydra-system' cookie to the value where this enterprise resides.
   * Consequently, checking if the application should be reload can be performed afterwards.
   * @param enterpriseName
   */
  getEnterpriseByNameOverRedirPath(enterpriseName: Identifier): Observable<Enterprise> {
    const url = `/middleware/api/redir/enterprise/${enterpriseName}`;
    return this.http
      .get<Enterprise>(url)
      .pipe(wrapError('enterprise-repository:get-enterprise-by-name-over-redir-path'));
  }

  getEnterpriseOptions(urlParams?: ApiUrlParams): Observable<EnterpriseOption[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/option',
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/option',
      [UserLevel.EndUser]: BaseUrls.EndUser + '/option',
    };

    return this.utils.apiUrl(urls, urlParams).pipe(
      mergeMap((url) => this.http.get<EnterpriseOption[]>(url)),
      wrapError('enterprise-repository:get-enterprise-options')
    );
  }

  listC5EndUsers(): Observable<EnhancedEndUser[]> {
    return this.utils.apiUrl('/c5EndUsers').pipe(
      mergeMap((url) => this.http.get<EndUserWithId[]>(url)),
      enhanceUsersWithExtension(),
      wrapError('enterprise-repository:list-c5-end-users')
    );
  }

  listEnterprises(noCache: boolean): Observable<DomainListItem[]> {
    const options = {
      headers: noCache ? { 'Cache-Control': 'no-cache' } : {},
    };

    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.ServiceProvider}/enterprises/list`,
      })
      .pipe(
        mergeMap((url) =>
          this.http.get<DomainListItem[]>(url, options).pipe(
            map((enterprises) =>
              enterprises.map((enterprise) => {
                return {
                  ...enterprise,
                  system: this.titleCasePipe.transform(enterprise.system),
                };
              })
            )
          )
        ),
        wrapError('enterprise-repository:list-enterprises')
      );
  }

  searchPhoneNumbers(searchParameters: SearchEnterprisePhoneNumbersParameters): Observable<PhoneNumber[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/phoneNumbers/search',
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/phoneNumbers/search',
    };

    if (searchParameters.internal != null) {
      searchParameters = {
        ...searchParameters,
        numberType: 'INTERNAL',
      };
    }

    return this.utils.apiUrl(urls).pipe(
      mergeMap((url) =>
        this.http.get<PhoneNumber[]>(url, {
          params: toHttpParams(searchParameters),
        })
      ),
      wrapError('enterprise-repository:search-enterprise')
    );
  }

  updateEnterprise(enterprise: Enterprise): Observable<void> {
    const url = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId`,
      [UserLevel.EnterpriseAdmin.toString()]: '/enterprise',
    };

    return this.utils.apiUrl(url).pipe(
      mergeMap((url) => this.http.put<void>(url, enterprise)),
      wrapError('enterprise-repository:update-enterprise')
    );
  }

  updateEnterpriseSBC(update: UpdateEnterpriseSBC, enterpriseName?: string): Observable<void> {
    const urls = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId/enterpriseSBC`,
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.Enterprise + `/enterpriseSBC`,
    };

    return this.utils
      .apiUrl(urls, {
        enterpriseId: enterpriseName,
      })
      .pipe(
        mergeMap((url) => this.http.put<void>(url, update)),
        wrapError('enterprise-repository:update-enterprise-sbc')
      );
  }

  getCivicAddress(): Observable<CivicAddress[]> {
    return this.utils.routeBasedApiUrl('/teams-partner/civic-addresses', UserLevel.EnterpriseAdmin).pipe(
      mergeMap((url) => this.http.get<CivicAddress[]>(url)),
      wrapError('enterprise-repository:get-civic-adresses')
    );
  }
}
